import React, { useState, useEffect } from 'react';
import { auth } from 'utils/firebaseConfig';
import { getMusicEnginePointsHistory, getUnifiedCurrentUser } from 'api/api';
import Header from 'components/Header/Header';
import PageSpinner from 'components/Spinner/PageSpinner';
import Styles from './rewardHistoryStyles.module.css';

function RewardHistoryPage(props) {
    const [rewardHistory, setRewardHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let unsub = null;

        const attemptLoad = async () => {
            try {
                const resp = await getMusicEnginePointsHistory(1, 50);
                const rows = Array.isArray(resp?.data) ? resp.data : [];
                setRewardHistory(rows);
                setLoading(false);
                // Once loaded successfully, stop listening to auth changes
                if (typeof unsub === 'function') { unsub(); unsub = null; }
            } catch (e) {
                // Keep spinner until auth resolves and we retry
            }
        };

        // Try immediately in case token already exists
        attemptLoad();

        // Also listen for Firebase auth; ensure MusicEngine token then retry
        unsub = auth.onAuthStateChanged(async (fbUser) => {
            try {
                if (fbUser) {
                    await getUnifiedCurrentUser();
                    await attemptLoad();
                } else {
                    // No user: show empty state instead of spinning forever
                    setLoading(false);
                }
            } catch (_) {
                // In any error, avoid infinite spinner if not authenticated
                if (!fbUser) setLoading(false);
            }
        });

        return () => { if (typeof unsub === 'function') unsub(); };
    }, []);

    useEffect(() => {
        document.addEventListener('keydown', onKeyDown);
        return () => { document.removeEventListener('keydown', onKeyDown); };
    }, []);

    const onKeyDown = (e) => {
        if (e.key === 'Backspace' || e.key === 'ArrowRight') {
            props.history.goBack();
        }
    };

    const backHandler = () => { props.history.push('/settings'); };

    const parsePretty = (row) => {
        const desc = (row?.description || '').toString();
        const lower = desc.toLowerCase();
        const clean = desc
            .replace(/\s*tag:(podcast|episode):[^\s]+/ig, '')
            .replace(/\s*hash:[a-f0-9]+/ig, '')
            .trim();
        let podcast = '';
        let episode = '';
        // New style descriptions
        const mLiked = clean.match(/^Liked episode: (.+?) \(Podcast: (.+?)\)/i);
        const mListened = clean.match(/^Listened episode: (.+?) \(Podcast: (.+?)\)/i);
        const mFollowed = clean.match(/^(Followed|Podcast followed)\s+podcast:?\s*(.+)$/i);
        if (mLiked) { episode = mLiked[1]; podcast = mLiked[2]; }
        else if (mListened) { episode = mListened[1]; podcast = mListened[2]; }
        else if (mFollowed) { podcast = mFollowed[2]; }
        // Legacy tag formats
        if (!podcast && lower.includes('tag:podcast:')) {
            const m = desc.match(/tag:podcast:([^\s]+)/i);
            if (m) podcast = m[1].replace(/\|/g, ' ').replace(/_/g, ' ').trim();
        }
        if (!episode && lower.includes('tag:episode:')) {
            const m = desc.match(/tag:episode:([^\s]+)/i);
            if (m) {
                const parts = m[1].split('|');
                if (parts.length >= 2) { podcast = podcast || parts[0]; episode = parts.slice(1).join('|'); }
            }
        }
        const sentence = (() => {
            const isDebitEvent = (
                (typeof row?.points === 'number' && row.points < 0) ||
                /redeem|donat|spend/i.test(lower) ||
                /redeem|donat|spend/i.test(String(row?.event_key || ''))
            );
            switch (row.event_key) {
                case 'podcast_follow':
                    return `Earned for following “${podcast || 'podcast'}”`;
                case 'episode_like':
                    return `Earned for liking “${episode || 'episode'}”${podcast ? ` under “${podcast}”` : ''}`;
                case 'episode_listen':
                    return `Earned for listening to “${episode || 'episode'}”${podcast ? ` under “${podcast}”` : ''}`;
                case 'profile_complete':
                    return `Earned for completing your profile`;
                default:
                    if (isDebitEvent) {
                        return clean || String(row.event_key || '').replace(/_/g, ' ');
                    }
                    return `Earned – ${clean || row.event_key}`;
            }
        })();
        return { sentence };
    };

    const fmtDateTime = (s) => {
        if (!s) return '';
        const d = new Date((s || '').replace(' ', 'T'));
        if (isNaN(d.getTime())) return '';
        const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
        const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
        return `${date} ${time}`;
    };

    const isDebit = (row) => {
        const key = (row?.event_key || '').toString().toLowerCase();
        const desc = (row?.description || '').toString().toLowerCase();
        if (typeof row?.points === 'number' && row.points < 0) return true;
        return key.includes('redeem') || key.includes('donat') || key.includes('spend') || desc.includes('redeem') || desc.includes('donat') || desc.includes('spend');
    };

    const getPointsView = (row) => {
        const debit = isDebit(row);
        const color = debit ? '#dc3545' : '#28a745';
        const pts = Math.abs(Number(row?.points || 0));
        const sign = debit ? '-' : '+';
        return { text: `${sign}${pts}`, color };
    };

    const goToRedemption = () => {
        props.history.push('/redemption');
    };

    return (
    <>
            <Header 
                title='Reward History' 
                back={true} 
                backHandler={backHandler}
                showRightButton={true}
                rightButtonText={'Redeem Points'}
                onRightButtonClick={goToRedemption}
            />

            {loading && <PageSpinner />}
            {rewardHistory.length > 0 && rewardHistory.map((row, i) => {
                const { sentence } = parsePretty(row);
                const { text, color } = getPointsView(row);
                const when = fmtDateTime(row.created_at);
                return (
                    <div key={i} style={{ padding: '10px 14px', borderBottom: '1px solid #eee' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ fontSize: '13px', lineHeight: 1.2 }} className={Styles.clamp2}>{sentence}</div>
                            <div style={{ fontSize: '16px', fontWeight: 700, color }}>{text}</div>
                        </div>
                        {!!when && (
                            <div style={{ marginTop: 2, fontSize: '11px', opacity: 0.6 }}>{when}</div>
                        )}
                    </div>
                );
            })}
            {
                !loading && rewardHistory.length === 0 &&
                <div className={Styles.container}>
                    No Rewards History
                </div>
            }
        </>
    );
}

export default RewardHistoryPage;